import { Board } from 'chessboard/model/board/types';
import type { Game } from 'chessboard/model/game/types';
import type { Core } from 'chessboard/core/config/types';
import type { EventEmitterUtil } from '@chesscom/web-ui';
import type { LegacyThemes } from 'chessboard/config/legacy-themes';
import type { TestElements } from 'src/utils/set-test-data';
import type { KeyedArrow } from 'chessboard/plugins/arrows/config/types';
import type {
  KeyedBlinkingHighlight,
  KeyedHighlight,
} from 'chessboard/plugins/highlights/config/types';
import type { KeyedEffect } from 'chessboard/plugins/effects/config/types';
import type { Effects } from 'chessboard/model/effects/types';
import type { BoardDOM } from 'chessboard/renderers/default/board/types';
import type { ThemeAssets } from 'chessboard/config/theme-assets';

export enum BoardClasses {
  Pseudo3d = 'pseudo3d',
  Disabled = 'board-disabled',
  Flipped = 'flipped',
}

export const enum AnimationTypes {
  All = 'All',
  Move = 'Move',
}

export interface DragPiece {
  coords?: Board.Coords;
  square: Board.SanSquare;
  toSquare?: Board.SanSquare;
}

export namespace CreateRenderer {
  export interface Generator {
    createRenderer: Model;
  }

  export type Model = ({ boardStyles, el, options, pieceStyles }: Options) => Renderer.Model;

  export interface Options {
    boardStyles?: LegacyThemes.BoardStyles;
    emitter?: EventEmitterUtil.EventEmitter;
    el: HTMLElement;
    options: Board.Options;
    pieceStyles?: LegacyThemes.PieceStyles;
    testElement?: TestElements;
  }
}

export type AddEffectsData = {
  effects: KeyedEffect[];
  translations: Effects.Translations;
};

export type RendererExtensionParams = {
  board: BoardDOM;
  el: HTMLElement;
  emitter: EventEmitterUtil.EventEmitter<Record<string, any>>;
  options: Board.Options<ThemeAssets.Assets>;
};

export namespace Renderer {
  export interface Model extends EventEmitterUtil.EventEmitter {
    [key: string]: any;

    animationComplete: (animationType: AnimationTypes) => Promise<void | boolean>;

    addAnalysisOverlay: () => void;

    addArrows: (arrows: KeyedArrow[], context: Core.Context) => void;

    addCaptureHints: (squares: Board.SanSquare[], context: Core.Context) => void;

    addMoveHints: (squares: Board.SanSquare[], context: Core.Context) => void;

    addHighlights: (highlights: KeyedHighlight[], context: Core.Context) => void;

    areAssetsLoaded: () => boolean;

    blinkHighlights: (blinkingHighlights: KeyedBlinkingHighlight[], context: Core.Context) => void;

    closePromotionWindow: (context: Core.Context) => void;

    dragPiece: (piece: DragPiece, context: Core.Context) => void;

    dropPiece: (square: Board.SanSquare, context: Core.Context) => void;

    destroy: () => void;

    extendRenderer: (fn: Function) => void;

    fadeSetup: (context: Core.Context) => void;

    flipBoard: (shouldFlip: boolean, context: Core.Context) => void;

    setBoardEnabled: (enabled: boolean) => void;

    getCoordsFromSquare: (square: Board.SanSquare, flipped: boolean) => Board.Coords;

    getEffectElements?: () => Map<string, HTMLDivElement[]>;

    getPieces: () => Game.Piece.ConciseObject;

    getPieceStyles?: () => LegacyThemes.PieceStyles;

    getPointerPosition: (options: {
      el: HTMLElement;
      event: any;
      flipped: boolean;
    }) => Board.PointerPosition | null;

    getBoardContainerAspectRatio: () => number;

    getWebGLCanvas?: () => HTMLCanvasElement;

    handleWebGLContextLost?: () => void;

    hideHoverSquare: (context: Core.Context) => void;

    illegalMove: (kingInCheck: false | Board.SanSquare, context: Core.Context) => void;

    isAnimating: (animationType?: AnimationTypes) => boolean;

    loadPieces: (
      {
        pieces,
        reloadAll,
      }: {
        pieces: Game.Piece.Collection;
        reloadAll?: boolean;
      },
      context: Core.Context,
    ) => void;

    makeMove: (move: Game.Move.Extended, context: Core.Context) => void;

    name: string;

    openPromotionWindow: (promotionMove: Game.Move.Model, context: Core.Context) => void;

    removeAnalysisOverlay: () => void;

    removeArrows: (arrows: KeyedArrow[], context: Core.Context) => void;

    removePiece: (square: Board.SanSquare, context: Core.Context) => void;

    removeHints: (squares: Board.SanSquare[], context: Core.Context) => void;

    removeHighlights: (highlights: KeyedHighlight[], context: Core.Context) => void;

    resize: (context: Core.Context) => void;

    setCoordinates: (
      {
        flipped,
        position,
      }: {
        flipped: boolean;
        position: Board.CoordinatesPositions;
      },
      context: Core.Context,
    ) => void;

    showHoverSquare: (square: Board.SanSquare, context: Core.Context) => void;

    undoMove: (move: Game.Move.ExtendedNode, context: Core.Context) => void;

    updateBoardImage: (context: Core.Context) => void;

    updatePieceBaseImage: (context: Core.Context, fallback?: boolean) => void;
  }

  export const enum Types {
    Default = 'default',
    WebGL2D = 'webgl-2d',
    WebGL3D = 'webgl-3d',
  }

  export const TriggerOptions = [
    Board.SaveableOptions.AnimationType,
    Board.SaveableOptions.PieceStyle,
  ];
}
